"use client";

import CardIcons from "@components/checkout/card-icons";
import { ColorModeContext, useCoupon } from "@contexts/index";
import { Box, Typography, useTheme } from "@mui/material";
import { useContext, useState, useEffect, useRef } from "react";
import { useAppTranslate } from "src/hooks/useAppTranslate";
import { formatCurrency } from "@configs/currencies";
import { MINIMUM_ORDER_CENTS } from "src/constants/minimum-order.constant";
import { TAX_VALUE } from "src/constants/tax.constant";
import {
  getCurrencyBasedOnDomain,
  getMarketDomain,
} from "src/utils/domain.util";
import { freeShippingState } from "src/utils/free-shipping";
import { isCashbackCoupon } from "src/utils/coupon-reward";
import { chromeSurface, chromeText } from "src/themes/chrome-surfaces";
/**
 * @param showShippingNote Whether to append the "+ shipping" caption.
 *
 * TRUE only while shipping is still unknown — the upload and photo-options
 * steps, where `currentPrice` is the print subtotal and delivery genuinely is
 * added later.
 *
 * FALSE on the delivery step, where the customer has already chosen a method
 * and `currentPrice` is derived from `order.amount`, which ALREADY CONTAINS
 * the shipping price. Appending "+ Dostava" there told the customer their
 * total was the sum plus shipping again: a real order read
 * "13,39 € + Dostava" when 13,39 was already 8,40 of prints plus 4,99 of
 * delivery. That is a 37% overstatement on the one screen where they decide
 * whether to go through with it.
 */
const CurrentPrice = ({
  currentPrice,
  showShippingNote = true,
  goodsCents,
}: {
  currentPrice: number | null;
  showShippingNote?: boolean;
  /**
   * The basket size to measure the free-shipping threshold against, in minor
   * units: pre-credit goods, shipping excluded (`goodsCents(order)`).
   *
   * Optional, so the steps that do not pass it keep the previous behaviour.
   * They should pass it: `currentPrice` is what the customer PAYS, which is
   * net of any credit and — on the delivery step — includes shipping, and
   * neither is what a basket-size threshold is asking about. Without it, EUR 10
   * of credit can make this bar say "+ Dostava" beside a progress bar on the
   * same page that says the order already qualifies.
   */
  goodsCents?: number | null;
}) => {
  //get theme
  const { mode, setMode } = useContext(ColorModeContext);
  const { coupon } = useCoupon();
  const translate = useAppTranslate();
  const minimumOrder = MINIMUM_ORDER_CENTS / 100;
  const priceWithoutTax =
    currentPrice !== null ? currentPrice / (1 + TAX_VALUE) : null;
  const currency = getCurrencyBasedOnDomain();

  // Check if we should show the price even when it's 0 due to a coupon. A
  // cashback coupon discounts nothing and never waives shipping, so a 0 here
  // is credit, not the coupon (final review I3).
  const hasCouponApplied = coupon != null && !isCashbackCoupon(coupon);
  // Show price whenever currentPrice is not null (i.e., photo size is selected)
  const shouldShowPrice = currentPrice !== null;

  // Use useState to track window width for client-side only
  const [windowWidth, setWindowWidth] = useState(0);

  // Update window width only on client side after component mounts
  useEffect(() => {
    setWindowWidth(window.innerWidth);

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  // Default to non-small screen for server-side rendering to avoid hydration mismatch
  const isSmallScreen = windowWidth > 0 ? windowWidth < 600 : false;
  const variant1 = isSmallScreen ? "body1" : "h6";
  const variant2 = isSmallScreen ? "body2" : "body1";

  /**
   * "+ shipping", sitting immediately after the price.
   *
   * No amount. The customer has not chosen home delivery or a parcel locker
   * yet, so any figure here would be a guess presented as a fact — and a
   * wrong-looking number is worse than an honest "there is shipping on top".
   * The actual cost, and how far they are from free, is on the cart and the
   * delivery step.
   *
   * Inline rather than a line of its own: this bar is fixed to the bottom of
   * a phone screen and already drops the payment icons below 600px. A caption
   * underneath added height to the one element that can least afford it.
   */
  const domain = getMarketDomain();
  const fs = freeShippingState(
    goodsCents ?? Math.round((currentPrice ?? 0) * 100),
    domain,
  );
  const shippingIsFree =
    fs?.qualified || (hasCouponApplied && currentPrice === 0);
  // order.shipping, not order.fields.shipping — the latter does not exist, so
  // the English fallback shipped and a Slovenian customer read "+ shipping".
  // Present in all eight locales: Dostava / Dostawa / Doručenie / Doprava /
  // Versand / Spedizione.
  //
  // Used exactly as translated. An earlier version lowercased it, which reads
  // naturally in Slovene and Polish and is simply wrong in German, where nouns
  // are capitalised: "+ versand". Casing belongs to the translation, not to
  // the component.
  const shippingNote = shippingIsFree
    ? translate("checkout.freeShippingUnlocked", "Free shipping")
    : `+ ${translate("order.shipping", "Shipping")}`;

  /**
   * Publish this bar's height so the sticky checkout button can clear it.
   *
   * The button sat at a hardcoded `bottom: 45px` under this bar, which is
   * fixed at bottom 0 with z-index 1000 against the button's 10. Measured: the
   * Slovenian label renders 365px wide at 16px bold, and a 390px phone has
   * 350px between the bar's paddings — so it wraps to two lines, stands about
   * 68px tall, and covers the lower third of the one button on the page that
   * takes the customer's money. Half of everyone who reaches the delivery step
   * never clicks pay.
   *
   * A CSS variable rather than a bigger magic number: the height depends on
   * the locale, the price, and whether the payment icons show, so any constant
   * is wrong on some phone in some language. Cleared on unmount so a page
   * without this bar falls back to the original offset.
   */
  const barRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const publish = () =>
      document.documentElement.style.setProperty(
        "--price-bar-height",
        `${Math.ceil(el.getBoundingClientRect().height)}px`,
      );
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty("--price-bar-height");
    };
  }, []);

  // Update the formatted price text logic to handle coupon scenarios
  // The pipe separated the label from the price; with the label gone on mobile
  // it would dangle at the start of the line.
  const formattedPriceText = shouldShowPrice
    ? isSmallScreen
      ? `(${translate("currentPrice.tax")})`
      : `(${translate("currentPrice.tax")}) | `
    : translate("currentPrice.NA");
  return (
    <>
      {/*
        Reserve the space the fixed furniture occupies.

        Both this bar and the sticky checkout button are position:fixed, so
        they sit outside normal flow and the page ends underneath them. On a
        phone that means the last screenful of every checkout step is covered:
        photographed on an iPhone, the NAPREJ button lands across the middle of
        an upsell card and slices it in half, and the bar hides whatever is
        below that.

        Only photo-options had a paddingBottom to compensate — delivery,
        additional-services and the order summary had none. Putting the spacer
        here rather than in each page means the next page to render a price bar
        gets the clearance for free instead of inheriting the bug.

        The button is fixed only below md, so only those widths reserve room
        for it as well as the bar.
      */}
      <Box
        aria-hidden
        sx={{
          height: {
            xs: "calc(var(--price-bar-height, 56px) + 64px)",
            sm: "calc(var(--price-bar-height, 56px) + 64px)",
            md: "var(--price-bar-height, 56px)",
          },
        }}
      />
      <Box
        ref={barRef}
        data-chat-avoid=""
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          // Light: primary.main / contrastText, as before. Dark: the shared
          // teal-ink chrome instead of a light-grey strip (chrome-surfaces).
          backgroundColor: (theme) =>
            chromeSurface(theme, theme.palette.primary.main),
          color: (theme) =>
            chromeText(theme, theme.palette.primary.contrastText),
          padding: "10px 20px",
          borderTop: 1,
          borderTopColor: "divider",
          textAlign: "center",
          zIndex: 1000,
        }}
      >
      {" "}
      {currentPrice !== null &&
        currentPrice > 0 &&
        currentPrice < minimumOrder && (
          <Typography variant="h6" color={"red"}>
            {translate("currentPrice.minimumOrder", {
              minimumOrder: formatCurrency(minimumOrder, currency),
            })}
          </Typography>
        )}
      {/*
        On a phone the label goes. "Trenutna cena (DDV vključen) | 27,00 € +
        Dostava" measures 365px at 16px bold against 350px of usable width, so
        it wrapped to two lines and the bar grew tall enough to sit under the
        checkout button. Dropping "Trenutna cena" and the pipe leaves
        "(DDV vključen) 27,00 € + Dostava", which fits on one line — and the
        label was never carrying its width: a price fixed to the bottom of a
        checkout screen is self-evidently the current price.

        The tax note stays. It is a price disclosure, not decoration.
      */}
      <Typography variant={variant1} sx={{ lineHeight: 1.3 }}>
        {!isSmallScreen && `${translate("currentPrice.currentPrice")} `}
        {formattedPriceText}{" "}
        {shouldShowPrice && (
          <strong>
            {hasCouponApplied && currentPrice === 0 ? (
              <span style={{ color: "green" }}>
                {translate("common.free", "FREE")}
              </span>
            ) : (
              <span>{formatCurrency(currentPrice ?? 0, currency)}</span>
            )}
            {/* Right after the figure, so the price and the fact that shipping
                is added to it are read as one thing. Measured: half of everyone
                who reaches the delivery step never clicks pay, in all four
                shops — that is where the total currently stops matching what
                they had in mind. */}
            {showShippingNote && (
              <Typography
                component="span"
                variant="caption"
                sx={{ fontWeight: 400, opacity: 0.85, ml: 0.75 }}
              >
                {shippingNote}
              </Typography>
            )}
            {!isSmallScreen && (
              <Box>
                <Typography variant={variant2}>
                  {translate("currentPrice.securePayment")}:{" "}
                  <CardIcons switchMode={false} />
                </Typography>
              </Box>
            )}
          </strong>
        )}
        </Typography>
      </Box>
    </>
  );
};

export default CurrentPrice;
